import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  effect,
  inject,
  input,
  signal,
} from '@angular/core';
import {
  FormBuilder,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
  ImageCropperComponent,
  ImageCroppedEvent,
  LoadedImage,
} from 'ngx-image-cropper';
import { auth as AuthGen } from '@valvur/shared-grpc';
import { AppToasterService } from '../../../../core/services/toaster.service';
import { getTakeUntilDestroyed } from '../../../../core/helpers/take-until-destroy.helper';
import { InitialsPipe } from '../../../../shared/pipes/initials.pipe';
import { InputFieldComponent } from '../../../../shared/components/ui/input-field/input-field.component';
import { ParentButtonComponent } from '../../../../shared/components/ui/parent-button/parent-button.component';
import { UserSharedService } from '../../service/user-shared.service';

type PersonalDataEditorLayout = 'admin' | 'patrol';

@Component({
  selector: 'app-personal-data-editor',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslatePipe,
    InputFieldComponent,
    InitialsPipe,
    ImageCropperComponent,
    ParentButtonComponent,
  ],
  templateUrl: './personal-data-editor.component.html',
  styleUrl: './personal-data-editor.component.scss',
})
export class PersonalDataEditorComponent {
  readonly user = input<AuthGen.User | undefined>(undefined);
  readonly layout = input<PersonalDataEditorLayout>('admin');

  readonly #fb = inject(FormBuilder);
  readonly #translate = inject(TranslateService);
  readonly #toaster = inject(AppToasterService);
  readonly #userShared = inject(UserSharedService);
  readonly #sanitizer = inject(DomSanitizer);
  readonly takeUntilDestroyed = getTakeUntilDestroyed();

  readonly form = this.#fb.group({
    firstName: this.#fb.nonNullable.control('', [Validators.required]),
    lastName: this.#fb.nonNullable.control('', [Validators.required]),
    email: this.#fb.nonNullable.control('', [
      Validators.required,
      Validators.email,
    ]),
    country: this.#fb.nonNullable.control(''),
    phone: this.#fb.nonNullable.control(''),
    address: this.#fb.nonNullable.control(''),
  });

  readonly isSaving = signal(false);
  readonly isUploading = signal(false);
  readonly showCropper = signal(false);
  readonly isImageLoading = signal(false);
  readonly hasImageError = signal(false);
  readonly isFileSizeError = signal(false);
  readonly formTick = signal(0);

  readonly firstNameValidation = signal<Record<string, string>>({
    required: '',
  });
  readonly lastNameValidation = signal<Record<string, string>>({
    required: '',
  });
  readonly emailValidation = signal<Record<string, string>>({
    required: '',
    email: '',
  });

  readonly canSave = computed(() => {
    this.formTick();
    return this.form.valid && this.form.dirty && !this.isSaving();
  });

  imageChangedEvent: Event | null = null;
  croppedImage: SafeUrl = '';
  private croppedImageBlob: Blob | null = null;
  private readonly MAX_FILE_SIZE = 1024 * 1024 * 3;

  constructor() {
    effect(() => {
      const user = this.user();
      const personalData = user?.personalData;

      this.form.reset(
        {
          firstName: personalData?.firstName ?? '',
          lastName: personalData?.lastName ?? '',
          email: personalData?.email ?? user?.userName ?? '',
          country: personalData?.country ?? '',
          phone: personalData?.phone ?? '',
          address: personalData?.address ?? '',
        },
        { emitEvent: false },
      );
      this.form.markAsPristine();
      this.form.markAsUntouched();
      this.formTick.update((value) => value + 1);
    });

    this.form.valueChanges.pipe(this.takeUntilDestroyed()).subscribe(() => {
      this.formTick.update((value) => value + 1);
    });
    this.form.statusChanges.pipe(this.takeUntilDestroyed()).subscribe(() => {
      this.formTick.update((value) => value + 1);
    });

    this.loadValidationMessages();
  }

  async save(): Promise<void> {
    if (!this.canSave()) {
      this.form.markAllAsTouched();
      this.formTick.update((value) => value + 1);
      return;
    }

    try {
      this.isSaving.set(true);
      const dto = this.form.getRawValue();
      await this.#userShared.updatePersonalInfo.mutateAsync(dto);
      this.form.markAsPristine();
      this.#toaster.success(
        this.#translate.instant('app.components.personalDataEditor.saved'),
      );
      this.formTick.update((value) => value + 1);
    } catch {
      this.#toaster.error(
        this.#translate.instant('app.components.personalDataEditor.saveError'),
      );
    } finally {
      this.isSaving.set(false);
    }
  }

  fileChangeEvent(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) {
      return;
    }

    const file = input.files[0];
    if (!file) {
      return;
    }

    this.showCropper.set(true);
    this.isImageLoading.set(true);
    this.hasImageError.set(false);
    this.isFileSizeError.set(false);

    setTimeout(() => {
      this.imageChangedEvent = event;
    }, 100);

    setTimeout(() => {
      if (this.isImageLoading()) {
        this.isImageLoading.set(false);
        this.hasImageError.set(true);
      }
    }, 10000);
  }

  imageCropped(event: ImageCroppedEvent): void {
    this.croppedImage = this.#sanitizer.bypassSecurityTrustUrl(
      event.objectUrl || '',
    );
    this.croppedImageBlob = event.blob || null;
  }

  imageLoaded(_: LoadedImage): void {
    this.isImageLoading.set(false);
    this.hasImageError.set(false);
    this.isFileSizeError.set(false);
  }

  cropperReady(): void {
    this.isImageLoading.set(false);
    this.hasImageError.set(false);
    this.isFileSizeError.set(false);
  }

  loadImageFailed(): void {
    this.isImageLoading.set(false);
    this.hasImageError.set(true);
    this.isFileSizeError.set(false);
  }

  isCropperReady(): boolean {
    return (
      !this.isImageLoading() &&
      !this.hasImageError() &&
      this.imageChangedEvent !== null
    );
  }

  retryImageLoad(): void {
    this.isImageLoading.set(true);
    this.hasImageError.set(false);
    this.isFileSizeError.set(false);
  }

  closeCropper(): void {
    this.showCropper.set(false);
    this.imageChangedEvent = null;
    this.croppedImage = '';
    this.croppedImageBlob = null;
    this.isImageLoading.set(false);
    this.hasImageError.set(false);
    this.isFileSizeError.set(false);
  }

  async saveCroppedImage(): Promise<void> {
    if (!this.croppedImageBlob) {
      return;
    }

    try {
      this.isUploading.set(true);
      const file = new File([this.croppedImageBlob], 'avatar.png', {
        type: 'image/png',
        lastModified: Date.now(),
      });

      if (file.size > this.MAX_FILE_SIZE) {
        this.hasImageError.set(true);
        this.isFileSizeError.set(true);
        this.isImageLoading.set(false);
        this.showCropper.set(true);
        return;
      }

      await this.#userShared.uploadAvatar.mutateAsync(file);
      this.closeCropper();
      this.#toaster.success(
        this.#translate.instant('app.components.personalDataEditor.avatarSaved'),
      );
    } catch {
      this.#toaster.error(
        this.#translate.instant(
          'app.components.personalDataEditor.avatarSaveError',
        ),
      );
    } finally {
      this.isUploading.set(false);
    }
  }

  async deleteAvatar(): Promise<void> {
    try {
      await this.#userShared.deleteAvatar.mutateAsync();
      this.#toaster.success(
        this.#translate.instant(
          'app.components.personalDataEditor.avatarDeleted',
        ),
      );
    } catch {
      this.#toaster.error(
        this.#translate.instant(
          'app.components.personalDataEditor.avatarDeleteError',
        ),
      );
    }
  }

  private loadValidationMessages(): void {
    this.#translate
      .get([
        'app.components.personalDataEditor.firstNameRequired',
        'app.components.personalDataEditor.lastNameRequired',
        'app.components.personalDataEditor.emailRequired',
        'app.components.personalDataEditor.emailInvalid',
      ])
      .pipe(this.takeUntilDestroyed())
      .subscribe((translations) => {
        this.firstNameValidation.set({
          required:
            translations['app.components.personalDataEditor.firstNameRequired'],
        });
        this.lastNameValidation.set({
          required:
            translations['app.components.personalDataEditor.lastNameRequired'],
        });
        this.emailValidation.set({
          required:
            translations['app.components.personalDataEditor.emailRequired'],
          email: translations['app.components.personalDataEditor.emailInvalid'],
        });
      });
  }
}
